import React, { useState } from "react";
import {
  ArrayInput,
  SimpleFormIterator,
  ReferenceInput,
  AutocompleteInput,
  FormDataConsumer,
  required,
} from "react-admin";
import { Grid, Typography } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import { useTranslate, useSimpleFormIteratorItem } from "react-admin";
import { Tooltip, IconButton } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import dataProvider from "server/dataProvider";
import { useFormContext } from "react-hook-form";
import ExamReOrderButtons from "../../Preliminary/Edit/ExamReOrderButtons";
import { settings } from "app";
import { ClassTypes } from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";

const roomOptionText = rec =>
  `${rec.roomName} - ${rec.buildingName}${rec.capacityExam ? ` (${rec.capacityExam})` : ""}`;

const MainRoomsList = ({ record, isDisabled }) => {
  const { setValue } = useFormContext();
  const notifyError = useNotifyError();
  const [excludedRoomsIds, setExcludedRoomsIds] = useState(record?.rooms?.map(room => room.roomId) || []);
  const [roomsCapacity, setRoomsCapacity] = useState(
    record?.rooms?.reduce((sum, room) => sum + room.capacity, 0) ?? null,
  );

  const fetchRoomsCapacity = async (roomIds, classTypes, showExamCapacity) => {
    if (roomIds.length === 0) {
      setRoomsCapacity(null);
      return;
    }

    try {
      const { data } = await dataProvider.customRequest(
        administrationRequests.getRoomsCapacitiesSelect(roomIds, classTypes, showExamCapacity),
        null,
        "GET",
      );

      setRoomsCapacity(data);

      setValue(`mainRoomsIds`, roomIds);
    } catch (error) {
      notifyError(error);
    }
  };

  const handleRoomChange = async (formDataRooms, classTypes, showExamCapacity) => {
    const selectedRoomsIds = formDataRooms.map(item => item.roomId).filter(Boolean);
    setExcludedRoomsIds(selectedRoomsIds);

    await fetchRoomsCapacity(selectedRoomsIds, classTypes, showExamCapacity);
  };

  const handleRemoveRoom = async (removedId, classTypes, showExamCapacity) => {
    const updatedExcludedRoomsIds = excludedRoomsIds.filter(id => id !== removedId);
    setExcludedRoomsIds(updatedExcludedRoomsIds);

    await fetchRoomsCapacity(updatedExcludedRoomsIds, classTypes, showExamCapacity);
  };

  const RemoveRoomButton = ({ record, classTypes, showExamCapacity, campaignIsStarted }) => {
    const translate = useTranslate();
    const { remove } = useSimpleFormIteratorItem();

    const handleClick = () => {
      if (record.roomId) {
        handleRemoveRoom(record.roomId, classTypes, showExamCapacity);
      }
      remove();
    };
    const title = translate("ra.action.remove");

    return (
      !campaignIsStarted && (
        <Tooltip title={title}>
          <IconButton
            color="warning"
            onClick={handleClick}
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      )
    );
  };

  return (
    <>
      <Grid
        item
        container
      >
        <Grid
          item
          xs={12}
          md={12}
        >
          <Typography color={"primary"}>Зали</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
        >
          <ArrayInput
            source="rooms"
            label=""
            defaultValue={[{}]}
          >
            <SimpleFormIterator
              inline
              reOrderButtons={!record?.campaignIsStarted && <ExamReOrderButtons />}
              disableRemove
              disableAdd={record?.campaignIsStarted}
              removeButton={!record?.campaignIsStarte}
              sx={settings.style.iteratorWithoutGap}
            >
              <FormDataConsumer>
                {({ formData, scopedFormData }) => {
                  const hasNoCandidacy = scopedFormData?.candidatesCount === 0;
                  return (
                    <Grid
                      container
                      spacing={1}
                    >
                      <Grid
                        item
                        xs={12}
                        md={10}
                      >
                        <ReferenceInput
                          source="roomId"
                          reference={administrationRequests.getRoomsSelect()}
                          filter={{
                            excludeIds: excludedRoomsIds.filter(Boolean),
                            filterByUser: true,
                            ClassTypes: ClassTypes.Csc,
                          }}
                        >
                          <AutocompleteInput
                            label="Зала"
                            optionValue="id"
                            optionText={roomOptionText}
                            inputText={roomOptionText}
                            matchSuggestion={() => true}
                            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                            onChange={() => handleRoomChange(formData.rooms, 1, true)}
                            fullWidth
                            readOnly={isDisabled}
                            validate={required()}
                          />
                        </ReferenceInput>
                      </Grid>
                      {hasNoCandidacy && (
                        <Grid
                          item
                          xs={12}
                          md={1}
                        >
                          <RemoveRoomButton
                            record={scopedFormData}
                            classTypes={ClassTypes.Csc}
                            showExamCapacity={true}
                            campaignIsStarted={record.campaignIsStarted}
                          />
                        </Grid>
                      )}
                    </Grid>
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>
      {roomsCapacity && (
        <Grid
          item
          xs={12}
          md={12}
          sx={{ paddingBottom: 5 }}
        >
          <Typography
            color="secondary"
            variant="h6"
          >
            Общ капацитет на залите: {roomsCapacity}
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default MainRoomsList;
